import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { ShieldCheck, FileCheck, AlertCircle, CheckCircle2, Clock, Send, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHECKLIST_ITEMS = [
  { key: 'company_registration', label: 'Company Registration Certificate', desc: 'Official certificate of incorporation or registration' },
  { key: 'insurance_certificate', label: 'Professional Insurance Certificate', desc: 'Professional indemnity and public liability insurance' },
  { key: 'dbs_policy', label: 'DBS Check Policy', desc: 'Policy for Disclosure and Barring Service checks' },
  { key: 'safeguarding_policy', label: 'Safeguarding Policy', desc: 'Adults and children safeguarding procedures' },
  { key: 'cqc_registration', label: 'CQC Registration', desc: 'Care Quality Commission registration (if applicable)' },
  { key: 'employer_liability', label: 'Employer Liability Insurance', desc: 'Employers liability insurance certificate' },
];

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: AlertCircle, color: 'text-muted-foreground', bg: 'bg-muted/50' },
  submitted: { label: 'Under Review', icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  approved: { label: 'Verified', icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
};

export default function AgencyVerification() {
  const [verification, setVerification] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/verification/status`, { withCredentials: true });
      setVerification(data);
      setChecklist(data.checklist || {});
    } catch { toast.error('Failed to load verification status'); }
    setLoading(false);
  };

  const toggleItem = (key) => {
    if (verification?.status === 'submitted' || verification?.status === 'approved') return;
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const submitVerification = async () => {
    const completedCount = Object.values(checklist).filter(Boolean).length;
    if (completedCount < 3) {
      toast.error('Please complete at least 3 checklist items before submitting');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/verification/submit`, { checklist }, { withCredentials: true });
      toast.success('Verification submitted for review');
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submission failed'); }
    setSubmitting(false);
  };

  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalItems = CHECKLIST_ITEMS.length;
  const progress = Math.round((completedCount / totalItems) * 100);
  const status = verification?.status || 'not_started';
  const StatusIcon = STATUS_CONFIG[status]?.icon || AlertCircle;
  const isLocked = status === 'submitted' || status === 'approved';

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="agency-verification-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Agency Verification</h1>
          <p className="text-muted-foreground mt-1">Complete the verification process to ensure compliance</p>
        </div>

        {/* Status Banner */}
        <Card className={`border-border ${STATUS_CONFIG[status]?.bg}`} data-testid="verification-status-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${STATUS_CONFIG[status]?.bg}`}>
                <StatusIcon className={`w-6 h-6 ${STATUS_CONFIG[status]?.color}`} />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${STATUS_CONFIG[status]?.color}`}>{STATUS_CONFIG[status]?.label}</h3>
                {verification?.submitted_at && (
                  <p className="text-sm text-muted-foreground">Submitted {new Date(verification.submitted_at).toLocaleDateString()}</p>
                )}
                {verification?.rejection_reason && (
                  <p className="text-sm text-red-400 mt-1">Reason: {verification.rejection_reason}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-foreground">{progress}%</p>
                <p className="text-xs text-muted-foreground">{completedCount}/{totalItems} complete</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-[#00A6A0] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Checklist */}
        <Card className="glass-card border-border" data-testid="verification-checklist">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-[#00A6A0]" />
              Compliance Checklist
            </CardTitle>
            <CardDescription>Confirm each requirement to submit for verification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {CHECKLIST_ITEMS.map(item => (
              <div
                key={item.key}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                  checklist[item.key]
                    ? 'border-[#00A6A0]/30 bg-[#00A6A0]/5'
                    : 'border-border bg-muted/20 hover:bg-muted/40'
                } ${isLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => toggleItem(item.key)}
                data-testid={`checklist-${item.key}`}
              >
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                  checklist[item.key] ? 'border-[#00A6A0] bg-[#00A6A0]' : 'border-muted-foreground/30'
                }`}>
                  {checklist[item.key] && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit */}
        {!isLocked && (
          <Button
            className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white h-12 text-base"
            onClick={submitVerification}
            disabled={submitting || completedCount < 3}
            data-testid="submit-verification-btn"
          >
            <Send className="w-5 h-5 mr-2" />
            {submitting ? 'Submitting...' : 'Submit for Verification'}
          </Button>
        )}
      </div>
    </DashboardLayout>
  );
}
